import { forwardRef, type AnchorHTMLAttributes, type ButtonHTMLAttributes, type ReactNode } from 'react';

import { cn } from '@/lib/cn';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'outline' | 'error' | 'destructive' | 'default' | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg';

type CommonProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
  children: ReactNode;
};

type ButtonProps = CommonProps & ButtonHTMLAttributes<HTMLButtonElement> & { href?: undefined };
type AnchorProps = CommonProps & AnchorHTMLAttributes<HTMLAnchorElement> & { href: string };

type PolymorphicProps = ButtonProps | AnchorProps;

const baseStyles =
  'inline-flex items-center justify-center gap-2 rounded-full font-medium tracking-tight transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-300 disabled:opacity-60 disabled:cursor-not-allowed';

const variantStyles: Record<ButtonVariant, string> = {
  primary:
    'bg-accent-500 text-white hover:bg-accent-400 active:bg-accent-600 shadow-[0_20px_60px_rgba(99,102,241,0.35)]',
  secondary: 'bg-white/10 text-white hover:bg-white/20 backdrop-blur-sm border border-white/10',
  ghost: 'bg-transparent text-white hover:bg-white/10',
  outline: 'border border-white/20 text-white hover:bg-white/10',
  error: 'bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30',
  destructive: 'bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30',
  default: 'bg-white/10 text-white hover:bg-white/20 backdrop-blur-sm border border-white/10',
  link: 'bg-transparent text-white/80 underline-offset-4 hover:underline hover:text-white',
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'px-4 py-2 text-sm',
  md: 'px-6 py-3 text-base',
  lg: 'px-8 py-4 text-lg'
};

export const Button = forwardRef<HTMLButtonElement, PolymorphicProps>((props, ref) => {
  const { variant = 'primary', size = 'md', className, children, ...rest } = props;
  const resolvedVariant = (variant === 'destructive' ? 'error' : variant === 'default' ? 'secondary' : variant === 'link' ? 'link' : variant) as ButtonVariant;

  if ('href' in rest && rest.href) {
    const anchorProps = rest as AnchorProps;
    return (
      <a
        {...anchorProps}
        className={cn(baseStyles, variantStyles[resolvedVariant], sizeStyles[size], className)}
      >
        {children}
      </a>
    );
  }

  const buttonProps = rest as ButtonProps;
  return (
    <button
      ref={ref}
      {...buttonProps}
      className={cn(baseStyles, variantStyles[resolvedVariant], sizeStyles[size], className)}
    >
      {children}
    </button>
  );
});

Button.displayName = 'Button';

export type { PolymorphicProps as ButtonProps };
